import React from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from "chart.js";
import { Spin } from "antd";
import styles from "./dashboard.module.css";

Chart.register(BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend);

interface LegendItemProps {
  color: string;
  text: string;
}

interface DashboardChartProps {
  chartData: any;
  selectedId: number | null;
  fetchProductStatus: (customerId: number) => Promise<void>;
  isLoading: boolean;
  duration?: string;
}

const DashboardChart: React.FC<DashboardChartProps> = ({
  chartData,
  isLoading,
  duration,
}) => {
  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: ["PRODUCT STATUS SUMMARY", duration || ""],
        font: { size: 16, weight: "bold" as const },
        color: "#333",
        padding: 20,
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            const item = tooltipItems[0];
            const stageName = item.label;
            const count = item.raw;
            return `${stageName} (${count})`;
          },
          label: (tooltipItem) => {
            const airtechModel = tooltipItem.dataset.label;
            return `${airtechModel || "-"}`;
          },
        },
      },
    },
    scales: {
      x: {
        type: "category",
        stacked: true,
        grid: { display: false },
        title: {
          display: true,
          text: "STAGES",
          color: "#333",
          font: { size: 14, weight: "bold" as const },
        },
      },
      y: {
        type: "linear",
        stacked: true,
        grid: { color: "#ccc" },
        title: {
          display: true,
          text: "COUNTS",
          color: "#333",
          font: { size: 14, weight: "bold" as const },
        },
      },
    },
  };

  const totalPlugin = {
    id: "totalPlugin",
    afterDatasetsDraw(chart: any) {
      const { ctx } = chart;
      const datasets = chart.data.datasets;
      const meta = chart.getDatasetMeta(0);
      meta.data.forEach((bar: any, index: number) => {
        const total = datasets.reduce(
          (sum: number, ds: any) => sum + ds.data[index],
          0
        );
        const x = bar.x;
        const topY = datasets.reduce(
          (minY: number, ds: any, i: number) =>
            Math.min(minY, chart.getDatasetMeta(i).data[index].y),
          Infinity
        );
        ctx.save();
        ctx.fillStyle = "#333";
        ctx.font = "bold 13px Arial";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText(`${total}`, x, topY - 6);
        ctx.restore();
      });
    },
  };

  return (
    <div>
      <div className={styles.chartContainer}>
        <div className={styles.chartInnerWrapper}>
          <div className={styles.chartBox}>
            {isLoading ? (
              <Spin size="large" />
            ) : chartData ? (
              <Bar data={chartData} options={options} plugins={[totalPlugin]} />
            ) : (
              <div className={styles.chartPlaceholder}>
                Select a customer to view data
              </div>
            )}
          </div>

          {chartData && (
            <div className={styles.legendContainer}>
              <h5 className={styles.legendTitle}>LEGEND</h5>
              {chartData.datasets.map((ds: any, i: number) => (
                <LegendItem
                  key={i}
                  color={ds.backgroundColor}
                  text={ds.label}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const LegendItem: React.FC<LegendItemProps> = ({ color, text }) => (
  <div className={styles.legendItem}>
    <div className={styles.legendColorBox} style={{ background: color }} />
    <span className={styles.legendText}>{text || "-"}</span>
  </div>
);

export default DashboardChart;
